import React, { useEffect, useState } from 'react';
import { useT } from '../i18n/LanguageContext';
import LanguageSwitcher from './LanguageSwitcher';

/**
 * Header — uses the original STIG ROCK logo image as-is, exactly as it
 * appears on stiggroup.com/stig-rock/. No CI modifications.
 */
export default function Header() {
  const { lang } = useT();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  // Auto-hide behaviour for presentation mode: header slides out of view
  // after a short delay and only comes back when the presenter moves
  // their pointer to the top edge of the screen or reveals the menu.
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Auto-hide: initial 2s grace period, then hide. Pointer within the
  // top 72 px of the viewport reveals the bar; leaving it hides again.
  useEffect(() => {
    let hideTimer = null;
    const HIDE_DELAY = 1600;
    const REVEAL_ZONE = 72;

    const schedule = () => {
      if (menuOpen) return;
      clearTimeout(hideTimer);
      hideTimer = setTimeout(() => setVisible(false), HIDE_DELAY);
    };

    const onMove = (e) => {
      if (e.clientY <= REVEAL_ZONE) {
        setVisible(true);
        clearTimeout(hideTimer);
      } else if (e.clientY > REVEAL_ZONE + 40) {
        schedule();
      }
    };

    const onTouchStart = (e) => {
      const y = e.touches?.[0]?.clientY ?? 999;
      if (y <= REVEAL_ZONE) setVisible(true);
    };

    schedule();
    window.addEventListener('mousemove', onMove, { passive: true });
    window.addEventListener('touchstart', onTouchStart, { passive: true });
    return () => {
      clearTimeout(hideTimer);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('touchstart', onTouchStart);
    };
  }, [menuOpen]);

  const goTo = (id) => {
    const el = document.querySelector(id);
    if (el && window.__lenis) window.__lenis.scrollTo(el, { duration: 1.5 });
    setMenuOpen(false);
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? 'glass' : 'bg-transparent'
      } ${visible ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0 pointer-events-none'}`}
    >
      <div className="mx-auto max-w-[1600px] flex items-center justify-between px-6 md:px-12 py-4">
        <a
          href="#hero"
          data-testid="logo-link"
          onClick={(e) => {
            e.preventDefault();
            window.__lenis?.scrollTo(0, { duration: 1.4 });
          }}
          className="flex items-center"
        >
          <img
            src="/brand/stig-rock-logo-white.png"
            alt="Stig Rock"
            className="h-12 md:h-14 w-auto select-none"
            draggable={false}
          />
        </a>

        <div className="flex items-center gap-4">
          <LanguageSwitcher variant="dark" />
        </div>
      </div>
    </header>
  );
}
